import React, { useState, useEffect } from 'react';
import { ArrowRight, Download, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import './App.css';
import './styles/Home.css';
import './styles/Buttons.css';
import {
  companyInfo,
  product,
  healthBenefits,
  labReports,
  clinicalSurvey,
  veganMovement,
  testimonials,
  storeLocations } from
'./mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const homeProductImages = [
  { type: "image", src: "https://customer-assets-cm19k8pv.emergentagent.net/job_5c3c04ce-a747-433e-adf6-ab76892df056/artifacts/qu0db6n4_NT-1-NB.png", alt: "Naru Tofu - Momen Firm Tofu Packaging" },
  { type: "video", src: "https://customer-assets-cm19k8pv.emergentagent.net/job_5c3c04ce-a747-433e-adf6-ab76892df056/artifacts/gou355kn_nt%20unboxing.mp4", alt: "Naru Tofu - Unboxing Video" },
  { type: "image", src: "https://customer-assets.emergentagent.com/job_transparent-tofu/artifacts/0swdh4dy_image.png", alt: "Naru Tofu - Momen Firm Tofu Block" }
];

const ProductImageSlider = () => {
  const [current, setCurrent] = useState(0);
  const next = () => setCurrent((p) => (p + 1) % homeProductImages.length);
  const prev = () => setCurrent((p) => (p - 1 + homeProductImages.length) % homeProductImages.length);
  return (
    <div className="product-image-container" style={{ position: 'relative' }}>
      {homeProductImages[current].type === 'video' ? (
        <video src={homeProductImages[current].src} className="product-image" style={{ width: '100%', borderRadius: '0px' }} autoPlay muted loop playsInline />
      ) : (
        <img src={homeProductImages[current].src} alt={homeProductImages[current].alt} className="product-image" style={{ width: '100%', borderRadius: '0px' }} />
      )}
      <button onClick={prev} aria-label="Previous" style={{ position:'absolute',left:'8px',top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,0.85)',border:'1px solid var(--border-light)',width:'36px',height:'36px',borderRadius:'50%',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><ChevronLeft size={18}/></button>
      <button onClick={next} aria-label="Next" style={{ position:'absolute',right:'8px',top:'50%',transform:'translateY(-50%)',background:'rgba(255,255,255,0.85)',border:'1px solid var(--border-light)',width:'36px',height:'36px',borderRadius:'50%',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}><ChevronRight size={18}/></button>
      <div style={{ display:'flex',justifyContent:'center',gap:'8px',marginTop:'12px' }}>
        {homeProductImages.map((_,i) => (
          <button key={i} onClick={() => setCurrent(i)} style={{ width:current===i?'24px':'8px',height:'8px',borderRadius:'4px',border:'none',cursor:'pointer',background:current===i?'var(--text-primary)':'var(--border-light)',transition:'all 0.3s ease' }}/>
        ))}
      </div>
    </div>
  );
};

// JSON-LD Structured Data
const structuredData = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Naru Tofu Organics Private Limited",
    "alternateName": "Naru Tofu",
    "url": "https://narutofu.com",
    "logo": "https://customer-assets.emergentagent.com/job_transparent-tofu/artifacts/xqb7fx6c_naru%20tofu%20logo.jpg",
    "description": "Premium fresh tofu and soymilk manufacturer in Bangalore, committed to transparency and quality",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Shop No 2, First Floor, Site No. 16 & 17, 1st Cross, Maruthi Layout, Katamnallur, Virgonagar Post",
      "addressLocality": "Bangalore",
      "addressRegion": "Karnataka",
      "postalCode": "560049",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-85948-35784",
      "contactType": "customer service",
      "email": "admin@narutofu.com",
      "availableLanguage": ["English", "Hindi"]
    },
    "sameAs": [
      "https://wa.me/919663840931"
    ]
  },
  localBusiness: {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Naru Tofu Organics Private Limited",
    "image": "https://customer-assets.emergentagent.com/job_transparent-tofu/artifacts/xqb7fx6c_naru%20tofu%20logo.jpg",
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Shop No 2, First Floor, Site No. 16 & 17, 1st Cross, Maruthi Layout, Katamnallur, Virgonagar Post",
      "addressLocality": "Bangalore",
      "addressRegion": "Karnataka",
      "postalCode": "560049",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "13.0827",
      "longitude": "77.5877"
    },
    "url": "https://narutofu.com",
    "telephone": "+91-85948-35784",
    "servesCuisine": "Japanese, Vegan",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  },
  product: {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Momen Firm Tofu Block",
    "image": "https://customer-assets.emergentagent.com/job_transparent-tofu/artifacts/0swdh4dy_image.png",
    "description": "Traditional Japanese-style firm tofu, handcrafted with premium non-GMO soybeans. Perfect texture for grilling, frying, or adding to your favorite dishes.",
    "brand": {
      "@type": "Brand",
      "name": "Naru Tofu"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "INR"
    },
    "nutrition": {
      "@type": "NutritionInformation",
      "calories": "118 kcal",
      "proteinContent": "15.8g per 100g",
      "carbohydrateContent": "3.4g per 100g",
      "fatContent": "4.6g per 100g"
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Preservatives",
        "value": "None"
      },
      {
        "@type": "PropertyValue",
        "name": "Cholesterol",
        "value": "Zero"
      }
    ]
  }
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inject JSON-LD structured data on component mount
  useEffect(() => {
    const scripts = [];
    
    const addSchema = (data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
      scripts.push(script);
    };

    addSchema(structuredData.organization);
    addSchema(structuredData.localBusiness);
    addSchema(structuredData.product);

    return () => {
      scripts.forEach(s => document.head.removeChild(s));
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);

      if (response.data.success) {
        toast.success(response.data.message, {
          description: 'We appreciate your interest in Naru Tofu.',
          duration: 5000
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to submit your message', {
        description: 'Please try again or contact us directly at admin@narutofu.com',
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = (reportTitle, downloadUrl) => {
    toast.info(`Downloading: ${reportTitle}`, {
      description: 'Your download will begin shortly.',
      duration: 3000
    });
    window.open(downloadUrl, '_blank');
  };

  return (
    <div className="home-page">{/* Hero Section */}
      <section className="hero-section section-padding">
        <div className="container">
          <div className="hero-content">
            <img 
              src="https://customer-assets.emergentagent.com/job_5c3c04ce-a747-433e-adf6-ab76892df056/artifacts/2z5lel2t_Gemini_Generated_Image_sv6x08sv6x08sv6x__1_-removebg-preview.png"
              alt="Naru Tofu Stamp"
              style={{ width: '180px', height: 'auto', margin: '0 auto 32px', display: 'block', background: 'transparent' }}
            />
            <h1 className="hero-large">{companyInfo.tagline}</h1>
            <p className="body-large hero-description">
              {companyInfo.description}
            </p>
            <div className="hero-buttons">
              <a
                href="https://wa.me/919663840931?text=Hi%2C%20I%27m%20interested%20in%20buying%20Naru%20Tofu%20products"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary">

                <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Buy Now on WhatsApp
              </a>
              <button className="btn-primary" onClick={() => document.getElementById('product').scrollIntoView({ behavior: 'smooth' })}>
                Discover our Tofu
                <ArrowRight size={16} />
              </button>
              <button className="btn-secondary" onClick={() => document.getElementById('reports').scrollIntoView({ behavior: 'smooth' })}>
                View Transparency Reports
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h2 className="hero-medium">Traditional Japanese craftsmanship meets Bangalore innovation</h2>
            </div>
            <div>
              <p className="body-large" style={{ marginBottom: '24px' }}>At Naru Tofu, we believe in absolute transparency. Every block of tofu we produce is crafted using traditional Japanese methods, premium non-GMO soybeans, and RO+UV Water — with zero preservatives.

              </p>
              <p className="body-large">
                We don't just make tofu. We make our processes, our ingredients, and our quality reports accessible to you. Because you deserve to know exactly what you're eating.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section id="product" className="product-section section-padding">
        <div className="container">
          <h2 className="hero-medium" style={{ textAlign: 'center', marginBottom: '60px' }}>Our Signature Product

          </h2>
          
          <div className="product-showcase">
            <ProductImageSlider />
            
            <div className="product-details">
              <h3 className="heading-1">{product.name}</h3>
              <p className="body-large product-description">{product.description}</p>
              
              <div className="product-specs">
                <div className="spec-item">
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Weight</span>
                  <span className="body-regular">{product.weight}</span>
                </div>
                <div className="spec-item">
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Protein</span>
                  <span className="body-regular">{product.protein}</span>
                </div>
              </div>
              
              <div className="product-features">
                {product.features.map((feature, index) =>
                <div key={feature} className="feature-item">
                    <CheckCircle2 size={16} color="var(--text-primary)" />
                    <span className="body-regular">{feature}</span>
                  </div>
                )}
              </div>
              
              <div className="nutritional-info">
                <h4 className="heading-3" style={{ marginBottom: '16px' }}>Nutritional Information (per 100g)</h4>
                <div className="nutrition-grid">
                  {Object.entries(product.nutritionalInfo).map(([key, value]) =>
                  <div key={key} className="nutrition-item">
                      <span className="body-small" style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                        {key}
                      </span>
                      <span className="body-regular">{value}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <a
                href="https://wa.me/919663840931?text=Hi%2C%20I%27d%20like%20to%20order%20Naru%20Tofu%20-%20Momen%20Firm%20Tofu%20Block"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ marginTop: '32px', width: '100%', textDecoration: 'none' }}>

                <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Buy Now on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Health Benefits Section */}
      <section id="benefits" className="benefits-section section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ textAlign: 'center', marginBottom: '60px' }}>
            Health Benefits of Tofu
          </h2>
          
          <div className="benefits-grid">
            {healthBenefits.map((benefit, index) =>
            <div key={benefit.title} className="benefit-card">
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>{benefit.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>{benefit.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Lab Reports Section */}
      <section id="reports" className="reports-section section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Transparency through Testing</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              See how our tofu exceeds industry standards
            </p>
          </div>
          
          <div className="reports-grid">
            {labReports.map((report) =>
            <div key={report.id} className="report-card hover-lift">
                <div className="report-header">
                  <h3 className="heading-2">{report.title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
                    {report.lab} • {report.date}
                  </p>
                </div>
                
                <div className="report-highlights">
                  {report.highlights.map((highlight, idx) =>
                <div key={idx} className="highlight-item">
                      <div className="highlight-metric">
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {highlight.metric}
                        </span>
                      </div>
                      <div className="highlight-comparison">
                        <div className="comparison-item">
                          <span className="body-small" style={{ fontWeight: '600' }}>Naru Tofu</span>
                          <span className="body-regular" style={{ color: highlight.better ? 'var(--status-success)' : 'var(--text-primary)' }}>
                            {highlight.naruValue}
                          </span>
                        </div>
                        <div className="comparison-item">
                          <span className="body-small" style={{ fontWeight: '600' }}>Industry Standard</span>
                          <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                            {highlight.industryStandard}
                          </span>
                        </div>
                      </div>
                    </div>
                )}
                </div>
                
                <button
                className="btn-icon"
                onClick={() => handleDownload(report.title, report.downloadUrl)}>

                  <Download size={16} />
                  Download full report
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Clinical Survey Section */}
      <section id="clinical" className="clinical-section section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>{clinicalSurvey.title}</h2>
              <div className="survey-meta">
                <p className="body-regular" style={{ marginBottom: '8px' }}>
                  <strong>Partner:</strong> {clinicalSurvey.partner}
                </p>
                <p className="body-regular" style={{ marginBottom: '8px' }}>
                  <strong>Duration:</strong> {clinicalSurvey.duration}
                </p>
              </div>
            </div>
            
            <div className="survey-stats">
              {clinicalSurvey.keyFindings.map((finding, index) => <div key={finding.statistic} className="stat-item">
                  <span className="stat-number">{finding.statistic}</span>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{finding.description}</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="doctor-quote">
            <p className="body-large" style={{ fontStyle: 'italic', marginBottom: '16px' }}>
              "{clinicalSurvey.doctorQuote}"
            </p>
            <div>
              <p className="body-regular" style={{ fontWeight: '600' }}>{clinicalSurvey.doctorName}</p>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{clinicalSurvey.doctorTitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vegan Movement Section */}
      <section id="vegan" className="vegan-section section-padding">
        <div className="container">
          <h2 className="hero-medium" style={{ textAlign: 'center', marginBottom: '24px' }}>
            {veganMovement.title}
          </h2>
          <p className="body-large" style={{ textAlign: 'center', marginBottom: '60px', maxWidth: '800px', margin: '0 auto 60px' }}>
            {veganMovement.mission}
          </p>
          
          <div className="initiatives-grid">
            {veganMovement.initiatives.map((initiative, index) =>
            <div key={index} className="initiative-card">
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>{initiative.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>{initiative.description}</p>
              </div>
            )}
          </div>
          
          {veganMovement.impact.length > 0 &&
          <div className="impact-stats">
              {veganMovement.impact.map((stat, index) =>
            <div key={stat.label} className="impact-item">
                  <span className="impact-number">{stat.number}</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{stat.label}</span>
                </div>
            )}
            </div>
          }
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>Get in touch</h2>
              <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
                If you manage a health-focused kitchen, school mess, hospital food service, or institutional meal program, we'd love to connect about clean, high-protein, cholesterol-free tofu.
              </p>
              
              <div className="contact-info">
                <div style={{ marginBottom: '24px' }}>
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Email</h4>
                  <p className="body-regular">{companyInfo.email}</p>
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Phone</h4>
                  <p className="body-regular">+91 85948 35784</p>
                  <p className="body-regular">+91 93390 29313</p>
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Manufacturing Unit</h4>
                  <p className="body-regular">{companyInfo.manufacturing.address}</p>
                  <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>FSSAI: {companyInfo.manufacturing.fssai}</p>
                </div>
              </div>
            </div>
            
            <div>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="body-regular">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input" />

                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="body-regular">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input" />

                </div>
                
                <div className="form-group">
                  <label htmlFor="phone" className="body-regular">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input" />

                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="body-regular">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    className="form-input" />

                </div>
                
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: '100%' }}
                  disabled={isSubmitting}>

                  {isSubmitting ? 'Sending...' : 'Send message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;